// 静态 JSON 数据 API 服务
// 所有数据从 public/data/ 目录的 JSON 文件读取

export interface Product {
  id: string;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  image: string;
  category?: string;
  features: string[];
  specifications?: Record<string, string>;
  sortOrder: number;
}

export interface Case {
  id: string;
  title: string;
  title_en?: string;
  description?: string;
  description_en?: string;
  image: string;
  client?: string;
  location?: string;
  date?: string;
  category?: 'ceramic' | 'steel' | 'other';
}

export interface Material {
  id: string;
  title: string;
  title_en?: string;
  description?: string;
  description_en?: string;
  image?: string;
  category?: 'measurement' | 'control' | 'refractory' | 'other';
  specifications?: Record<string, string>;
  features?: string[];
  applications?: string[];
}

export interface Article {
  id: string;
  title: string;
  title_en?: string;
  content: string;
  content_en?: string;
  summary?: string;
  excerpt?: string;
  image?: string;
  author?: string;
  date?: string;
  publishedAt?: string;
  category?: 'ceramic' | 'steel' | 'other' | 'technology';
  tags?: string[];
  readTime?: string;
  slug?: string;
  status: 'draft' | 'published';
  pdfFile?: string;
}

export interface Banner {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  image: string;
  link?: string;
  sortOrder: number;
  active: boolean;
}

class ApiService {
  private async fetchJSON<T>(filename: string): Promise<T[]> {
    try {
      const response = await fetch(`/data/${filename}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${filename}`);
      }
      const data = await response.json();
      // JSON 文件格式: { "products": [...] } 或 { "cases": [...] } 等
      const key = filename.replace('.json', '');
      return data[key] || [];
    } catch (error) {
      console.error(`Error loading ${filename}:`, error);
      return [];
    }
  }

  // 产品相关API
  async getProducts(params?: {
    sort?: string;
    limit?: number;
  }): Promise<Product[]> {
    let products = await this.fetchJSON<Product>('products.json');

    // 排序
    if (params?.sort) {
      products = products.sort((a, b) => {
        if (params.sort === 'sortOrder') {
          return a.sortOrder - b.sortOrder;
        }
        return 0;
      });
    }

    // 限制数量
    if (params?.limit) {
      products = products.slice(0, params.limit);
    }

    return products;
  }

  async getProduct(id: string): Promise<Product | null> {
    const products = await this.fetchJSON<Product>('products.json');
    return products.find(p => p.id === id) || null;
  }

  // 案例相关API
  async getCaseStudies(params?: {
    sort?: string;
    limit?: number;
  }): Promise<Case[]> {
    let cases = await this.fetchJSON<Case>('cases.json');

    // 排序
    if (params?.sort === '-date') {
      cases = cases.sort((a, b) => {
        if (a.date && b.date) {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
        return 0;
      });
    }

    // 限制数量
    if (params?.limit) {
      cases = cases.slice(0, params.limit);
    }

    return cases;
  }

  async getCaseStudy(id: string): Promise<Case | null> {
    const cases = await this.fetchJSON<Case>('cases.json');
    return cases.find(c => c.id === id) || null;
  }

  // 文章相关API
  async getArticles(params?: {
    sort?: string;
    limit?: number;
  }): Promise<Article[]> {
    let articles = await this.fetchJSON<Article>('articles.json');

    // 只返回已发布的文章
    articles = articles.filter(a => a.status === 'published');

    // 排序
    if (params?.sort === '-publishedAt') {
      articles = articles.sort((a, b) => {
        if (a.publishedAt && b.publishedAt) {
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        }
        return 0;
      });
    }

    // 限制数量
    if (params?.limit) {
      articles = articles.slice(0, params.limit);
    }

    return articles;
  }

  async getArticle(id: string): Promise<Article | null> {
    const articles = await this.fetchJSON<Article>('articles.json');
    return articles.find(a => a.id === id && a.status === 'published') || null;
  }

  // 轮播图相关API
  async getBanners(params?: {
    sort?: string;
    limit?: number;
  }): Promise<Banner[]> {
    let banners = await this.fetchJSON<Banner>('banners.json');

    // 只返回启用的横幅
    banners = banners.filter(b => b.active);

    // 排序
    if (params?.sort === 'sortOrder') {
      banners = banners.sort((a, b) => a.sortOrder - b.sortOrder);
    }

    // 限制数量
    if (params?.limit) {
      banners = banners.slice(0, params.limit);
    }

    return banners;
  }

  // 获取媒体文件URL（图片路径直接使用）
  getMediaUrl(path: string | null | undefined): string {
    if (!path) return '/images/placeholder.jpg';
    return path;
  }

  // 获取缩略图URL（简化版，直接返回原图）
  getThumbnailUrl(path: string | null | undefined): string {
    return this.getMediaUrl(path);
  }

  // 器件材料相关API
  async getMaterials(params?: {
    sort?: string;
    limit?: number;
  }): Promise<Material[]> {
    let materials = await this.fetchJSON<Material>('materials.json');

    // 默认按ID排序（数字ID按数值排序，否则按字符串排序）
    materials = materials.sort((a, b) => {
      const aNum = Number(a.id);
      const bNum = Number(b.id);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.id.localeCompare(b.id);
    });

    // 限制数量
    if (params?.limit) {
      materials = materials.slice(0, params.limit);
    }

    return materials;
  }

  async getMaterial(id: string): Promise<Material | null> {
    const materials = await this.fetchJSON<Material>('materials.json');
    return materials.find(m => m.id === id) || null;
  }
}

export const apiService = new ApiService();
export default apiService;
